import {
    createContext,
    useEffect,
    useState
} from "react";
import type {
    ReactNode
} from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    SERVICE_EVENT_TYPES
} from "../services/restAPI/constants";
import RESTServiceContext from "../services/restAPI/context";

export interface User {
    organizations?: { id: string; name: string; profilePicturePath?: string; features?: string[] }[];
    adminType?: "root" | "superAdmin" | "admin" | "user" | "guest";
    type?: "root" | "superAdmin" | "admin" | "user" | "guest";
    role: "root" | "superAdmin" | "admin" | "user" | "guest";
    profilePicturePath?: string;
    org_id?: string | null;
    profilePhoto?: string;
    name_surname?: string;
    phoneNumber?: string;

    firstName?: string;
    org_ids?: string[];
    lastName?: string;
    fullName?: string;
    username?: string;
    adminID?: string;
    address?: string;
    email?: string;
    phone?: string;
    mail?: string;
    id?: string;
}

interface AuthContextType {
    changeActiveOrganization: (orgId: string | null, features?: string[]) => Promise<void>;
    login: (token: string, user: User, refreshToken?: string) => Promise<void>;
    updateUser: (data: Partial<User>) => Promise<void>;
    activeOrganizationFeatures: string[];
    activeOrganizationId: string | null;
    guestLogin: () => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
    user: User | null;
    loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
    changeActiveOrganization: async () => {},
    activeOrganizationFeatures: [],
    activeOrganizationId: null,
    guestLogin: async () => {},
    updateUser: async () => {},
    isAuthenticated: false,
    logout: async () => {},
    login: async () => {},
    loading: true,
    user: null
});

export const AuthProvider = ({
    children
}: { children: ReactNode }) => {
    const [
        activeOrganizationId,
        setActiveOrganizationId
    ] = useState<string | null>(null);
    const [
        activeOrganizationFeatures,
        setActiveOrganizationFeatures
    ] = useState<string[]>([]);
    const [
        isAuthenticated,
        setIsAuthenticated
    ] = useState(false);
    const [
        user,
        setUser
    ] = useState<User | null>(null);
    const [
        loading,
        setLoading
    ] = useState(true);

    const logout = async () => {
        try {
            await AsyncStorage.removeItem("activeOrganizationId");
            await AsyncStorage.removeItem("authToken");
            await AsyncStorage.removeItem("authUser");
            await AsyncStorage.removeItem("authGuest");
            await AsyncStorage.removeItem("activeOrganizationFeatures");
            await AsyncStorage.removeItem("authRefreshToken");
            setUser(null);
            setIsAuthenticated(false);
        } catch (error) {
            console.error("Logout sırasında hata:", error);
        }
    };

    useEffect(() => {
        const handleLogout = () => {
            logout();
        };

        const handleUserUpdated = (updatedInfo: Partial<User>) => {
            setUser(prev => {
                if (!prev) return null;
                const newUser = {
                    ...prev,
                    ...updatedInfo,
                    role: updatedInfo.type || updatedInfo.adminType || prev.role || "admin"
                } as User;
                AsyncStorage.setItem("authUser", JSON.stringify(newUser)).catch(() => {});
                return newUser;
            });
        };

        RESTServiceContext.addEventListener(SERVICE_EVENT_TYPES.GO_TO_LOGOUT, handleLogout);
        RESTServiceContext.addEventListener(SERVICE_EVENT_TYPES.USER_UPDATED, handleUserUpdated);

        return () => {
            RESTServiceContext.removeEventListener(SERVICE_EVENT_TYPES.GO_TO_LOGOUT);
            RESTServiceContext.removeEventListener(SERVICE_EVENT_TYPES.USER_UPDATED);
        };
    }, []);

    useEffect(() => {
        const loadAuth = async () => {
            try {
                const token = await AsyncStorage.getItem("authToken");
                const userData = await AsyncStorage.getItem("authUser");

                if (token && userData) {
                    let parsedUser = JSON.parse(userData) as User;
                    parsedUser = {
                        ...parsedUser,
                        role: parsedUser.type || parsedUser.adminType || parsedUser.role || "admin"
                    } as User;
                    setUser(parsedUser);
                    setIsAuthenticated(true);

                    try {
                        const result = await RESTServiceContext.action("me", null);

                        if (result.code === 200 && result.payload) {
                            const fetchedUser = result.payload;
                            parsedUser = {
                                ...fetchedUser,
                                role: fetchedUser.type || fetchedUser.adminType || "admin"
                            } as User;
                            setUser(parsedUser);
                            await AsyncStorage.setItem("authUser", JSON.stringify(parsedUser));
                        }
                    } catch (e: unknown) {
                        const error = e as Error;
                        console.error("User fetch err:", error);
                        if (error?.message === "session-ended") {
                            setLoading(false);
                            return;
                        }
                    }

                    if (parsedUser.role === "root") {
                        const savedOrgId = await AsyncStorage.getItem("activeOrganizationId");
                        const savedFeatures = await AsyncStorage.getItem("activeOrganizationFeatures");
                        if (savedOrgId) {
                            setActiveOrganizationId(savedOrgId);
                        }
                        if (savedFeatures) {
                            try {
                                setActiveOrganizationFeatures(JSON.parse(savedFeatures));
                            } catch(e) {
                                console.error("err:", e);
                            }
                        }
                    } else if (parsedUser.organizations && parsedUser.organizations.length > 0) {
                        const savedOrgId = await AsyncStorage.getItem("activeOrganizationId");
                        let selectedOrg = parsedUser.organizations[0];
                        if (savedOrgId) {
                            const found = parsedUser.organizations.find(o => o.id === savedOrgId);
                            if (found) {
                                selectedOrg = found;
                            }
                        }
                        setActiveOrganizationId(selectedOrg?.id || null);

                        if (selectedOrg?.id) {
                            try {
                                const response = await fetch(`${process.env.EXPO_PUBLIC_API_URL || "http://localhost:83/api/v1"}/organizations/${selectedOrg.id}`, {
                                    headers: {
                                        Authorization: `Bearer ${token}`
                                    }
                                });
                                const result = await response.json();
                                if (result.status === "success" && result.data) {
                                    setActiveOrganizationFeatures(result.data.features || []);
                                    selectedOrg.features = result.data.features || [];
                                    await AsyncStorage.setItem("authUser", JSON.stringify(parsedUser));
                                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(result.data.features || []));
                                } else {
                                    setActiveOrganizationFeatures(selectedOrg?.features || []);
                                }
                            } catch {
                                setActiveOrganizationFeatures(selectedOrg?.features || []);
                            }
                        } else {
                            setActiveOrganizationFeatures(selectedOrg?.features || []);
                        }
                    }
                } else {
                    await AsyncStorage.removeItem("authGuest");
                }
            } catch (error) {
                console.error("Auth yüklenirken hata:", error);
            } finally {
                setLoading(false);
            }
        };

        loadAuth();
    }, []);

    const updateUser = async (data: Partial<User>) => {
        if (!user) return;
        const updatedUser = {
            ...user,
            ...data
        };
        setUser(updatedUser);
        await AsyncStorage.setItem("authUser", JSON.stringify(updatedUser));
    };

    const login = async (token: string, userData: User, refreshToken?: string) => {
        try {
            const parsedUserData = {
                ...userData,
                role: userData.type || userData.adminType || userData.role || "admin"
            } as User;

            setUser(parsedUserData);
            setIsAuthenticated(true);

            await AsyncStorage.setItem("authToken", token);
            if (refreshToken) {
                await AsyncStorage.setItem("authRefreshToken", refreshToken);
            }
            await AsyncStorage.setItem("authUser", JSON.stringify(parsedUserData));

            if (parsedUserData.organizations && parsedUserData.organizations.length > 0) {
                const org = parsedUserData.organizations[0];
                setActiveOrganizationId(org?.id || null);
                setActiveOrganizationFeatures(org?.features || []);
                if (org?.id) {
                    await AsyncStorage.setItem("activeOrganizationId", org.id);
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(org.features || []));
                }
            } else {
                setActiveOrganizationId(null);
                await AsyncStorage.removeItem("activeOrganizationId");
            }
        } catch (error) {
            console.error("Login işlemi kaydedilirken hata:", error);
        }
    };

    const guestLogin = async () => {
        // Misafir girişi tamamen engellendi.
        return Promise.resolve();
    };

    const changeActiveOrganization = async (orgId: string | null, features?: string[]) => {
        try {
            if (user?.role !== "root" && orgId) {
                const token = await AsyncStorage.getItem("authToken");
                if (token) {
                    try {
                        const result = await RESTServiceContext.action("switchOrganization", {
                            organization_id: orgId
                        });

                        if (result.code === 200 && result.token) {
                            await AsyncStorage.setItem("authToken", result.token as string);
                        }
                    } catch (error) {
                        console.error("Organizasyon değiştirme başarısız.", error);
                        return;
                    }
                }
            }

            setActiveOrganizationId(orgId);
            if (features) {
                setActiveOrganizationFeatures(features);
            } else if (orgId && user?.organizations) {
                const found = user.organizations.find(o => o.id === orgId);
                setActiveOrganizationFeatures(found?.features || []);
            } else {
                setActiveOrganizationFeatures([]);
            }

            if (orgId) {
                await AsyncStorage.setItem("activeOrganizationId", orgId);
                if (features) {
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(features));
                } else if (user?.organizations) {
                    const found = user.organizations.find(o => o.id === orgId);
                    await AsyncStorage.setItem("activeOrganizationFeatures", JSON.stringify(found?.features || []));
                }
            } else {
                await AsyncStorage.removeItem("activeOrganizationId");
                await AsyncStorage.removeItem("activeOrganizationFeatures");
            }
        } catch (error) {
            console.error("Aktif organizasyon kaydedilirken hata:", error);
        }
    };

    return <AuthContext.Provider
        value={{
            activeOrganizationFeatures,
            changeActiveOrganization,
            activeOrganizationId,
            isAuthenticated,
            guestLogin,
            updateUser,
            loading,
            logout,
            login,
            user
        }}
    >
        {children}
    </AuthContext.Provider>;
};
